import { buildTransformationString, injectCloudinaryTransformations } from 'shared/types/utils/cloudinary';
import { CloudinaryLoaderProps } from './types';

let hasWarnedMissingConfig = false;

/**
 * Next writes loader URLs into `srcset`, where whitespace separates a URL from its width descriptor,
 * and `#`/`?` would end the path early. A raw space in a public ID (e.g. "Poplar - Unfinished") makes
 * the browser drop every candidate and fall back to the largest — a 3000px download for a 40px
 * swatch. Public IDs are raw (unencoded) from Cloudinary, so each path segment is percent-encoded;
 * Cloudinary decodes them back to the same asset. An ID that already carries escapes is left alone
 * apart from spaces, so it is never double-encoded.
 */
const encodePublicId = (publicId: string) =>
  /%[0-9A-Fa-f]{2}/.test(publicId)
    ? publicId.replace(/ /g, '%20')
    : publicId.split('/').map(encodeURIComponent).join('/');

/** Fallback placeholder image path - returned when no valid source is provided */
const PLACEHOLDER_IMAGE = '/images/placeholder.png';

/**
 * Preset configuration for Cloudinary image transformations.
 *
 * Values are stored **unprefixed** (e.g., 'fill' not 'c_fill') to match the
 * shared `buildTransformationString` convention. Cloudinary prefixes (c_, q_,
 * g_, ar_) are added by `buildTransformationString` at build time.
 *
 * Each preset defines optimization settings for a specific use case:
 * - quality: Quality value (auto, 80, auto:good) — prefixed with q_ at build time
 * - crop: Cropping mode (limit, fill) — prefixed with c_ at build time
 * - gravity: Focus point for cropping (auto, undefined for no gravity) — prefixed with g_ at build time
 * - aspectRatio: Force aspect ratio (1:1 for squares, undefined to preserve) — prefixed with ar_ at build time
 * - maxWidth: Cap output width to optimize bandwidth (undefined for no cap)
 * - effects: Chained Cloudinary effects applied after base transformations — kept prefixed (e.g., ['e_brightness:-3'])
 */
const PRESET_CONFIGS = {
  thumbnail: {
    quality: '80',
    crop: 'fill',
    gravity: 'auto',
    aspectRatio: '1:1',
    maxWidth: undefined,
  },
  gallery: {
    quality: 'auto',
    crop: 'limit',
    gravity: undefined,
    aspectRatio: undefined,
    maxWidth: 1440, // 960px gallery + headroom for 2x DPI
  },
  modal: {
    quality: 'auto:good',
    crop: 'limit',
    gravity: undefined,
    aspectRatio: undefined,
    maxWidth: 2560, // 1440p at 2x DPI, most 4K displays
  },
  plp: {
    quality: 'auto',
    crop: 'limit',
    gravity: undefined,
    aspectRatio: undefined,
    maxWidth: 960, // ~430px max rendered × 2x DPI + headroom
    effects: ['e_brightness:-3'], // slight darkening to improve contrast on white tile backgrounds
  },
  default: {
    quality: 'auto',
    crop: 'limit',
    gravity: undefined,
    aspectRatio: undefined,
    maxWidth: undefined,
  },
} as const;

/**
 * Cloudinary image loader with preset optimization strategies.
 *
 * Generates optimized Cloudinary URLs with transformations based on use case.
 *
 * @param src - Source image URL
 * @param mediaId - Cloudinary media ID (alternative to src)
 * @param width - Target width in pixels
 * @param height - Target height in pixels
 * @param ratio - Aspect ratio (e.g., "16:9", "1:1") - applies cropping
 * @param gravity - Focal point for cropping (e.g., "auto", "face", "custom")
 * @param x - X coordinate for custom gravity
 * @param y - Y coordinate for custom gravity
 * @param scaleBy - Scale by 'width' or 'height'
 * @param preset - Optimization preset:
 *   - 'thumbnail': Lower quality (80), aggressive optimization, crop to fill
 *   - 'gallery': Balanced quality (auto), scale to fit, max 1440px
 *   - 'modal': High quality (auto:good), for full-screen viewing, max 2560px
 *   - 'plp': Product listing tiles (auto), scale to fit, max 960px
 *   - 'default': Standard quality (auto), original behavior
 *
 * @returns {string} Cloudinary URL with transformations, or original src if configuration is missing
 *
 * @example
 * // Thumbnail with square crop
 * cloudinaryLoader({ preset: 'thumbnail', width: 112 })
 * // Result: w_112,ar_1:1,c_fill,g_auto,q_80,f_auto
 *
 * @example
 * // Gallery image with responsive width
 * cloudinaryLoader({ preset: 'gallery', width: 960 })
 * // Result: w_960,c_limit,q_auto,f_auto
 */
export function cloudinaryLoader({
  src,
  mediaId,
  width,
  ratio,
  gravity,
  x,
  y,
  height,
  scaleBy = 'width',
  preset = 'default',
}: CloudinaryLoaderProps) {
  // Early return for empty inputs - return placeholder to avoid failed network request
  if (!mediaId && !src) {
    return PLACEHOLDER_IMAGE;
  }

  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
  const hostname = process.env.NEXT_PUBLIC_CLOUDINARY_HOSTNAME;

  // Fall back to original source if Cloudinary is not configured
  if (!cloudName && !hostname) {
    if (!hasWarnedMissingConfig && process.env.NODE_ENV !== 'test') {
      // Using console.error intentionally (not warn) - unoptimized images significantly
      // impact performance, bandwidth costs, and Core Web Vitals. This must be fixed.
      console.error('[cloudinaryLoader] Cloudinary configuration missing. Images will load without optimization.');
      hasWarnedMissingConfig = true;
    }
    return src;
  }

  const root = `https://${hostname ? hostname : `res.cloudinary.com/${cloudName}`}/image/upload`;

  // Fallback to default preset if invalid value is passed (e.g., from CMS/API data)
  const isValidPreset = preset in PRESET_CONFIGS;
  if (!isValidPreset && process.env.NODE_ENV === 'development') {
    console.warn(
      `[cloudinaryLoader] Invalid preset "${preset}". Expected one of: ${Object.keys(PRESET_CONFIGS).join(', ')}. Falling back to "default".`,
    );
  }
  const presetConfig = isValidPreset ? PRESET_CONFIGS[preset] : PRESET_CONFIGS.default;

  const effects = 'effects' in presetConfig && presetConfig.effects.length ? presetConfig.effects : undefined;

  let transformations: string;

  if (ratio) {
    // Ratio mode: fill crop with explicit aspect ratio
    transformations = buildTransformationString({
      width: width && presetConfig.maxWidth ? Math.min(width, presetConfig.maxWidth) : width,
      aspectRatio: ratio,
      crop: 'fill',
      gravity: gravity ?? 'auto',
      ...(gravity === 'custom' ? { x, y } : {}),
      quality: presetConfig.quality,
      format: 'auto',
      effects,
    });
  } else {
    // Preset mode: dimension-based scaling
    const effectiveWidth = width && presetConfig.maxWidth ? Math.min(width, presetConfig.maxWidth) : width;
    const dimensionWidth = scaleBy === 'width' && effectiveWidth ? effectiveWidth : undefined;
    const dimensionHeight = scaleBy === 'height' && height ? height : undefined;

    if (!dimensionWidth && !dimensionHeight) {
      const minimal = effects ? 'f_auto/' + effects.join(',') : 'f_auto';
      return mediaId
        ? `${root}/${minimal}/${encodePublicId(mediaId)}`
        : injectCloudinaryTransformations(src, minimal).replace(/ /g, '%20');
    }

    transformations = buildTransformationString({
      width: dimensionWidth,
      height: dimensionHeight,
      aspectRatio: presetConfig.aspectRatio,
      crop: presetConfig.crop,
      gravity: presetConfig.gravity,
      quality: presetConfig.quality,
      format: 'auto',
      effects,
    });
  }

  // If mediaId is provided, construct URL directly with transformations.
  // Otherwise, attempt to inject transformations into existing Cloudinary URL.
  // If src doesn't match the expected pattern (e.g., non-Cloudinary URL),
  // the injection won't modify the URL and src is returned unchanged - this is intentional.
  return mediaId
    ? `${root}/${transformations}/${encodePublicId(mediaId)}`
    : // A src URL should already be encoded; only stray spaces (which break srcset) are fixed up.
      injectCloudinaryTransformations(src, transformations).replace(/ /g, '%20');
}
